import { IconAlertCircle, IconChevronDown, IconCircleCheck, IconTool } from '@tabler/icons-react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  SvgIcon,
  Typography,
} from '@mui/material'
import type { ToolCall } from '../api'
import { CopyToClipboardIconButton } from '~/ui/components/CopyToClipboardIconButton'

interface Props {
  toolCall: ToolCall
}

const formatToolName = (name: string) =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export const ToolCallBlock = ({ toolCall }: Props) => {
  const isRunning = toolCall.status === 'running'

  const toolResult = (() => {
    if (!toolCall.result) return ''
    try {
      return JSON.stringify(JSON.parse(toolCall.result), null, 2)
    } catch {
      return toolCall.result
    }
  })()
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
        mb: 0.5,
      }}
    >
      <AccordionSummary
        expandIcon={
          <SvgIcon sx={{ fontSize: 16 }}>
            <IconChevronDown />
          </SvgIcon>
        }
        sx={{ minHeight: 36, py: 0, px: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isRunning ? (
            <CircularProgress size={14} />
          ) : toolCall.isError ? (
            <SvgIcon sx={{ fontSize: 16, color: 'error.main' }}>
              <IconAlertCircle />
            </SvgIcon>
          ) : (
            <SvgIcon sx={{ fontSize: 16, color: 'success.main' }}>
              <IconCircleCheck />
            </SvgIcon>
          )}
          <SvgIcon sx={{ fontSize: 14, color: 'text.secondary' }}>
            <IconTool />
          </SvgIcon>
          <Typography variant="caption" fontWeight={500}>
            {formatToolName(toolCall.toolName)}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
        {Object.keys(toolCall.input).length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Input
            </Typography>

            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 11,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                position: 'relative',
              }}
            >
              <Box sx={{ position: 'absolute', top: 0, right: 0, m: 1 }}>
                <CopyToClipboardIconButton
                  content={JSON.stringify(toolCall.input, null, 2)}
                  tooltip="Copy to clipboard"
                />
              </Box>
              {JSON.stringify(toolCall.input, null, 2)}
            </Box>
          </Box>
        )}

        {toolCall.result && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Result
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: toolCall.isError ? 'error.50' : 'action.hover',
                borderRadius: 1,
                fontSize: 11,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                position: 'relative',
              }}
            >
              <Box sx={{ position: 'absolute', top: 0, right: 0, m: 1 }}>
                <CopyToClipboardIconButton content={toolResult} tooltip="Copy to clipboard" />
              </Box>
              {toolResult}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}
